import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  REPORTING_LE_LENS_SPACES_PATH,
  reportingLeLensSpaceMembersPath,
  reportingLeLensSpacePath,
} from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { request } from '~/services/common';

import type { LensLibraryAccess, LensSpace, LensSpaceMember } from '~/types/leLens';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

export const lensSpaceQueryKeys = {
  all: ['reporting', 'le-lens', 'spaces'] as const,
  lists: () => [...lensSpaceQueryKeys.all, 'list'] as const,
  browsable: () => [...lensSpaceQueryKeys.all, 'browsable'] as const,
  detail: (spaceId: string) => [...lensSpaceQueryKeys.all, 'detail', spaceId] as const,
};

type SpacesResponse = { spaces: Array<LensSpace>; libraryAccess: LensLibraryAccess };
type SpaceResponse = { space: LensSpace | null; members: Array<LensSpaceMember> };

/**
 * Every team space the signed-in employee belongs to, with its membership.
 *
 * Spaces they are not in are not returned: the library's Team spaces destination
 * is their spaces, not a directory of the company's.
 */
export function useGetLensSpaces(enabled = true) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.lists(), user?.email],
    enabled: enabled && Boolean(user?.email),
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_SPACES_PATH), {
        signal,
      })) as SpacesResponse;
      return response.spaces ?? [];
    },
    retry: false,
  });
}

/** Every folder the reader can browse (theirs, every open one, all of them for a reporting admin), and whether they may create one. */
export function useGetBrowsableLensSpaces(enabled = true) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.browsable(), user?.email],
    enabled: enabled && Boolean(user?.email),
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENS_SPACES_PATH}?all=true`), {
        signal,
      })) as Partial<SpacesResponse>;
      return {
        spaces: response.spaces ?? [],
        // Older reporting snapshots omit library permissions. Navigation remains readable;
        // reviewer and creation actions stay hidden until the server explicitly grants them.
        libraryAccess: response.libraryAccess ?? {
          canSeeEveryLens: false,
          canReviewCertification: false,
          canCreateFolder: false,
        },
      };
    },
    retry: false,
  });
}

/** One folder with its people, readable by anyone who can browse it. */
export function useGetLensSpace(spaceId: string | null) {
  return useQuery({
    queryKey: lensSpaceQueryKeys.detail(spaceId ?? ''),
    enabled: Boolean(spaceId),
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSpacePath(spaceId!)), { signal })) as {
        space: LensSpace;
        members: Array<LensSpaceMember>;
      },
    retry: false,
  });
}

/** Invalidates spaces and the Lens list together: a space change moves rows between destinations. */
function useSpaceMutation<TInput>(
  mutationFn: (input: TInput) => Promise<SpaceResponse>,
  successMessage: (result: SpaceResponse) => string,
) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn,
    onSuccess: async (result) => {
      enqueueSnackbar(successMessage(result), { variant: 'success' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: lensSpaceQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() }),
      ]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'We couldn’t save this team space. Try again.', { variant: 'error' });
    },
  });
}

export function useCreateLensSpace() {
  return useSpaceMutation<{
    name: string;
    description: string | null;
    managers?: Array<string>;
    audience: LensSpace['audience'];
  }>(
    async (input) =>
      (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_SPACES_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeRequestBody(input)),
      })) as SpaceResponse,
    (result) => `${result.space?.name ?? 'Team space'} created`,
  );
}

export function useUpdateLensSpace() {
  return useSpaceMutation<{
    spaceId: string;
    name: string;
    description: string | null;
    audience: LensSpace['audience'];
  }>(
    async ({ spaceId, ...body }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSpacePath(spaceId)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeRequestBody(body)),
      })) as SpaceResponse,
    (result) => `${result.space?.name ?? 'Team space'} updated`,
  );
}

/**
 * Replaces the membership in one write. The list sent is the list that remains,
 * so a removal is an omission and the dialog names that before it sends.
 */
export function useUpdateLensSpaceMembers() {
  return useSpaceMutation<{ spaceId: string; members: Array<{ email: string; role: LensSpaceMember['role'] }> }>(
    async ({ spaceId, members }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSpaceMembersPath(spaceId)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ members }),
      })) as SpaceResponse,
    (result) => `${result.members.length} ${result.members.length === 1 ? 'member' : 'members'} in this team space`,
  );
}
